/**
 * Tooltip — one tooltip per chart, positioned INSIDE the chart container (the mockup's `.tip`),
 * shown on hover AND on keyboard focus of a mark (spec §6/§8: "אותו מידע במיקוד מקלדת").
 * On touch the caller pins it; Escape closes.
 * Content is structured data, never raw HTML.
 */

import { useCallback, useState } from 'react';
import type { FocusEvent, PointerEvent as ReactPointerEvent } from 'react';

export interface TipRow {
  label: string;
  value: string;
  /** Series swatch colour, e.g. `var(--series-1)`. */
  color?: string;
  /** Square swatch (stacked/grouped columns) instead of the default line swatch. */
  square?: boolean;
}

export interface TipContent {
  title: string;
  rows: TipRow[];
}

export interface TipState {
  content: TipContent;
  /** Position inside the chart container, in CSS px. */
  x: number;
  y: number;
  pinned: boolean;
}

export interface TooltipApi {
  state: TipState | null;
  show: (content: TipContent, x: number, y: number) => void;
  hide: () => void;
  togglePin: () => void;
  /** Handlers for a mark: hover follows the pointer, focus anchors to the mark's box. */
  bind: (content: TipContent, anchor?: { x: number; y: number }) => {
    onPointerEnter: (event: ReactPointerEvent) => void;
    onPointerMove: (event: ReactPointerEvent) => void;
    onPointerLeave: () => void;
    onFocus: (event: FocusEvent) => void;
    onBlur: () => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
  };
}

function localPoint(target: Element, clientX: number, clientY: number): { x: number; y: number } {
  const host = target.closest('.chart-viz') ?? target.closest('.chart-tap') ?? target;
  const rect = host.getBoundingClientRect();
  return { x: clientX - rect.left, y: clientY - rect.top };
}

export function useTooltip(): TooltipApi {
  const [state, setState] = useState<TipState | null>(null);

  const hide = useCallback(() => setState((prev) => (prev?.pinned ? prev : null)), []);
  const show = useCallback((content: TipContent, x: number, y: number) => setState((prev) => ({ content, x, y, pinned: prev?.pinned ?? false })), []);
  const togglePin = useCallback(() => setState((prev) => (prev ? { ...prev, pinned: !prev.pinned } : prev)), []);

  const bind = useCallback(
    (content: TipContent, anchor?: { x: number; y: number }) => ({
      onPointerEnter: (event: ReactPointerEvent) => {
        if (event.pointerType === 'touch') return;
        const p = anchor ?? localPoint(event.currentTarget as Element, event.clientX, event.clientY);
        show(content, p.x, p.y);
      },
      onPointerMove: (event: ReactPointerEvent) => {
        if (event.pointerType === 'touch') return;
        const p = anchor ?? localPoint(event.currentTarget as Element, event.clientX, event.clientY);
        show(content, p.x, p.y);
      },
      onPointerLeave: hide,
      onFocus: (event: FocusEvent) => {
        const el = event.target as Element;
        const host = el.closest('.chart-viz') ?? el;
        const box = el.getBoundingClientRect();
        const hostBox = host.getBoundingClientRect();
        show(content, anchor?.x ?? box.left - hostBox.left + box.width / 2, anchor?.y ?? box.top - hostBox.top);
      },
      onBlur: () => setState(null),
      onKeyDown: (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') setState(null);
      },
    }),
    [hide, show],
  );

  return { state, show, hide, togglePin, bind };
}

/** The tooltip element. Render it as the last child of the `.chart-viz` container. */
export function ChartTip({ state, width = 720 }: { state: TipState | null; width?: number }) {
  if (!state) return null;
  const left = Math.max(0, Math.min(state.x + 12, width - 170));
  const top = Math.max(0, state.y - 8);
  return (
    <div className="tip" role="status" style={{ left: `${left}px`, top: `${top}px` }}>
      <div className="tt">
        <span>{state.content.title}</span>
      </div>
      {state.content.rows.map((row, index) => (
        <div className="tr" key={index}>
          <span className="tk">
            {row.color ? <i className={row.square ? 'sq' : undefined} style={{ background: row.color }} /> : null}
            {row.label}
          </span>
          <b>{row.value}</b>
        </div>
      ))}
    </div>
  );
}
